import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, RefreshCw, Utensils, Wine, ArrowDown, Pizza, GlassWater } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { RestaurantBrand, ThemeToggle, menuPrice } from "@/components/RestaurantBrand";
import CreditFooter from "@/components/CreditFooter";
import AllergenDialog from "@/components/AllergenDialog";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage, useStrings, makeTranslator } from "@/lib/i18n";
import api from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";
import { groupMenuItems } from "@/lib/menuGroups";

const renderPizzaDescription = (text) => {
  const match = /dopo cottura:|after baking:/i.exec(text);
  if (!match) return text;
  const idx = match.index;
  return <>{text.slice(0, idx)}<span className="after-bake">{match[0].toLowerCase()}</span>{text.slice(idx + match[0].length)}</>;
};

export const MenuItem = ({ item, pizza = false, tr = (k, f) => f, strings }) => (
  <li className="menu-item" data-testid={`menu-item-${item.id}`}>
    <div className="min-w-0">
      <p className="menu-item-name" data-testid={`menu-item-name-${item.id}`}>{item.name}</p>
      {item.description && <p className="menu-item-description" data-testid={`menu-item-description-${item.id}`}>{(() => {
        const text = tr(`product:${item.id}:description`, item.description);
        return pizza ? renderPizzaDescription(text) : text;
      })()}</p>}
    </div>
    <div className="menu-item-price" data-testid={`menu-item-price-${item.id}`}>
      {menuPrice(item.price)}{item.price_unit === "kg" && <span className="price-unit">{strings?.perKg || "al kg"}</span>}
    </div>
  </li>
);

export const MenuCategory = ({ category, items, subcategories = [], wine = false, pizza = false, tr = (k, f) => f, strings }) => {
  const groups = groupMenuItems(items, subcategories);
  const pizzaStyle = ["crema", "bianco", "nero"].includes(category.style) ? category.style : "crema";
  const className = wine ? `wine-panel wine-panel-${category.style}` : pizza ? `pizza-panel pizza-panel-${pizzaStyle}` : "food-category";
  return (
    <section id={`category-${category.id}`} className={className} aria-labelledby={`title-${category.id}`} data-testid={`menu-category-${category.id}`}>
      <h2 className="category-title" id={`title-${category.id}`} data-testid={`menu-category-title-${category.id}`}>{tr(`category:${category.id}:name`, category.name)}</h2>
      {groups.map(({ id, label, items: entries }) => <div className="menu-subsection" key={id || "none"} data-testid={`menu-group-${category.id}-${id || "none"}`}>
        {label && <h3 className="subsection-title" data-testid={`menu-subsection-${category.id}-${id}`}>{tr(`subcategory:${id}:name`, label)}</h3>}
        <ul className="menu-items">{entries.map(item => <MenuItem item={item} pizza={pizza} tr={tr} strings={strings} key={item.id} />)}</ul>
      </div>)}
      {category.note && <p className="category-note" data-testid={`menu-category-note-${category.id}`}>{tr(`category:${category.id}:note`, category.note)}</p>}
    </section>
  );
};

export default function CustomerOrderPage() {
  usePageTitle("Menu ristorante");
  const [lang, setLang] = useLanguage();
  const strings = useStrings(lang);
  const [tab, setTab] = useState("cucina");
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (quiet = false) => {
    if (!quiet) { setLoading(true); setError(false); }
    try {
      const { data } = await api.get("/public/menu");
      setMenu(data);
      setError(false);
    } catch { if (!quiet) setError(true); }
    finally { if (!quiet) setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const refresh = () => { if (!document.hidden) load(true); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);
  const tr = useMemo(() => makeTranslator(lang, menu?.translations), [lang, menu]);
  const byCategory = useMemo(() => {
    const result = {};
    for (const item of menu?.products || []) (result[item.category_id] ||= []).push(item);
    return result;
  }, [menu]);
  const categories = (menu?.categories || []).filter(c => c.section === tab && (byCategory[c.id]?.length || c.note));
  const count = categories.reduce((sum, c) => sum + (byCategory[c.id]?.length || 0), 0);
  const caption = { cucina: strings.captionCucina, bevande: strings.captionBevande, pizze: strings.captionPizze, vini: strings.captionVini }[tab];
  const countLabel = { cucina: strings.countCucina, bevande: strings.countBevande, pizze: strings.countPizze, vini: strings.countVini }[tab];
  return (
    <div className={`restaurant-page ${tab === "vini" ? "nz-wine" : ""} ${tab === "pizze" ? "nz-pizza" : ""}`} data-testid="restaurant-menu">
      <a href="#menu-content" className="skip-link" data-testid="skip-to-menu">{strings.skipToMenu}</a>
      <header className="menu-header menu-container" data-testid="menu-header">
        <p className="menu-eyebrow" data-testid="menu-brand-label">Nove Zero Due<span>Restaurant & More</span></p>
        <Link to="/menu" aria-label="Nove Zero Due — Menu" data-testid="menu-home-link" onClick={() => setTab("cucina")}>{tab === "pizze" ? <img key="pizza-logo" src="/pizza-logo.webp" alt="In — Pizza Contemporanea & Food Experience" className="pizza-header-logo logo-fade" data-testid="menu-logo" /> : <RestaurantBrand key="restaurant-logo" className="logo-fade" testId="menu-logo" />}</Link>
        <div className="header-tools"><LanguageToggle lang={lang} onChange={setLang} strings={strings} /><ThemeToggle /></div>
      </header>
      <Tabs value={tab} onValueChange={setTab} className="menu-tabs" data-testid="menu-tabs">
        <nav className="menu-navigation" aria-label="Sezioni del menu">
          <TabsList className="menu-tab-list">
            <TabsTrigger value="cucina" className="menu-tab" data-testid="menu-tab-cucina"><Utensils /> {strings.tabCucina}</TabsTrigger>
            <TabsTrigger value="bevande" className="menu-tab" data-testid="menu-tab-bevande"><GlassWater /> {strings.tabBevande}</TabsTrigger>
            <TabsTrigger value="pizze" className="menu-tab" data-testid="menu-tab-pizze"><Pizza /> {strings.tabPizze}</TabsTrigger>
            <TabsTrigger value="vini" className="menu-tab" data-testid="menu-tab-vini"><Wine /> {strings.tabVini}</TabsTrigger>
          </TabsList>
        </nav>
        <main id="menu-content" className="menu-container" tabIndex={-1}>
          <div className="menu-intro">
            <h1 key={`${tab}-${lang}`} className="logo-fade" data-testid="menu-title">{tab === "cucina" ? <img src={lang === "en" ? "/chef-heading-en.png" : "/chef-heading.png"} alt={strings.chefHeadingAlt} className="chef-heading" /> : tab === "bevande" ? <img src={lang === "en" ? "/drinks-heading-en.png" : "/drinks-heading.png"} alt={strings.drinksHeadingAlt} className="chef-heading drinks-heading" /> : tab === "pizze" ? <span className="pizza-heading">{strings.pizzaHeading}</span> : <img src={lang === "en" ? "/wine-heading-en.png" : "/wine-heading.png"} alt={strings.wineHeadingAlt} className="wine-heading" />}</h1>
            <div className="menu-caption" data-testid="menu-caption"><span>{caption}</span><span>{!loading && !error ? `${count} ${countLabel}` : "Nove Zero Due"}<ArrowDown aria-hidden="true" /></span></div>
          </div>
          {loading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-16" aria-label={strings.loadingMenu} data-testid="menu-loading">{[0,1,2,3].map(i => <Skeleton key={i} className="h-48 w-full" />)}</div> : error ? <div className="menu-state" role="alert" data-testid="menu-error"><h2>{strings.errorTitle}</h2><p>{strings.errorBody}</p><Button onClick={() => load()} data-testid="menu-retry"><RefreshCw /> {strings.retry}</Button></div> : <>
            {["cucina", "bevande", "pizze", "vini"].map(section => <TabsContent key={section} value={section} className="mt-0" data-testid={`menu-content-${section}`}>
              {categories.length ? <div className={section === "cucina" || section === "bevande" ? "food-grid" : "wine-grid"}>
                {categories.map(c => <MenuCategory key={c.id} category={c} items={byCategory[c.id] || []} subcategories={menu.subcategories} wine={section === "vini"} pizza={section === "pizze"} tr={tr} strings={strings} />)}
              </div> : <div className="menu-state" data-testid="menu-empty"><h2>{strings.emptyTitle}</h2><p>{strings.emptyBody}</p><Button onClick={() => load()} data-testid="menu-empty-refresh"><RefreshCw /> {strings.refreshMenu}</Button></div>}
            </TabsContent>)}
            <div className="menu-notes">
              <div className="menu-notes-row">
                {tab !== "vini" && <p className="cover-charge" data-testid="menu-cover-charge">{strings.coverCharge} <strong>{menuPrice(menu.settings.cover_charge)}</strong></p>}
                <AllergenDialog lang={lang} strings={strings} />
              </div>
              {menu.settings.note && <p className="general-note" data-testid="menu-general-note">{tr("settings:note", menu.settings.note)}</p>}
            </div>
          </>}
        </main>
      </Tabs>
      <footer className="menu-footer menu-container">
        <p data-testid="restaurant-footer-brand">NOVE ZERO DUE <span>RESTAURANT & MORE</span></p>
        <Button asChild variant="link" className="admin-access" data-testid="admin-access-link"><Link to="/admin/login">{strings.adminArea} <ArrowUpRight /></Link></Button>
        <CreditFooter className="restaurant-credit" />
      </footer>
    </div>
  );
}
